import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import type { NavItem } from '@/entities/session';
import { ROUTES } from '@/shared/config';
import { cn } from '@/shared/lib';
import { sectionIcon } from '../lib/sectionIcons';
import { groupNav } from '../model/nav';

export interface SidebarNavProps {
  nav: readonly NavItem[];
}

/**
 * Меню сайдбара (README §1): группы с заголовком 10.5/600 uppercase gold-leaf-3;
 * пункт 37 / radius 12 / gap 9, полоска 2×16 (активный — wine-bordeaux), иконка 16,
 * текст 13.5/18 500; активный — фон paper-pure и ink-900, обычный — ink-700; hover — #E3D3B4.
 * Бейдж справа (`badge` пункта из `/admin/me`: «Забота» — новые и просроченные обращения):
 * min-width 18, высота 16, radius 18, фон wine-bordeaux, текст белый 10/16 600.
 */
export function SidebarNav({ nav }: SidebarNavProps) {
  const { t } = useTranslation('shell');
  const groups = useMemo(() => groupNav(nav), [nav]);
  return (
    <nav
      aria-label={t('nav.label')}
      className="relative flex min-h-0 flex-1 flex-col gap-14 overflow-y-auto px-12 pb-16"
    >
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-2">
          <h2 className="pt-7 pr-8 pb-5 pl-12 text-[10.5px] font-semibold tracking-[0.11em] text-gold-leaf-3 uppercase">
            {group.group}
          </h2>
          <ul className="flex flex-col gap-2">
            {group.items.map((item) => {
              const Icon = sectionIcon(item.key);
              return (
                <li key={item.key}>
                  <NavLink
                    to={ROUTES.section(item.key)}
                    className={({ isActive }) =>
                      cn(
                        'group flex h-37 items-center gap-9 rounded-12 pr-8 no-underline transition-colors duration-(--duration-fast)',
                        'hover:bg-sidebar-hover',
                        isActive ? 'bg-paper-pure text-ink-900' : 'bg-transparent text-ink-700',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          aria-hidden="true"
                          className={cn(
                            'h-16 w-2 shrink-0',
                            isActive ? 'bg-wine-bordeaux' : 'bg-transparent',
                          )}
                        />
                        <Icon size={16} className="shrink-0" />
                        <span className="truncate text-[13.5px] leading-[18px] font-medium">
                          {item.label}
                        </span>
                        <span className="flex-1" />
                        {item.badge ? (
                          <>
                            <span
                              aria-hidden="true"
                              className="flex h-16 min-w-18 shrink-0 items-center justify-center rounded-18 bg-wine-bordeaux px-5 text-[10px] leading-[16px] font-semibold text-paper-pure tabular"
                            >
                              {item.badge}
                            </span>
                            <span className="sr-only">{t('nav.badge', { count: item.badge })}</span>
                          </>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
